import { useEffect, useMemo, useState } from "react";
import {
    Button,
    Checkbox,
    Divider,
    Group,
    Modal,
    NumberInput,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Text,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { IconFileExport } from "@tabler/icons-react";
import dayjs from "dayjs";
import { ChatBotHistoryExportRequest, postApiChatBotHistoryExportCsv } from "@/API";
import { DateToTimestamp, TimestampToDate, getEndTimePresets, getStartTimePresets } from "@/Helpers/DateHelper";
import { BlobSaveToFile } from "@/Helpers/SaveToFile";
import { getTimeZoneOptions, getUserTimeZone } from "@/Helpers/TimeZoneHelper";

export interface ChatBotHistoryExportModalProps {
    opened: boolean;
    onClose: () => void;
    defaultSearch?: Partial<ChatBotHistoryExportRequest>;
    title?: string;
}

export function ChatBotHistoryExportModal({
    opened,
    onClose,
    defaultSearch = {},
    title = "匯出聊天紀錄",
}: ChatBotHistoryExportModalProps) {
    const [loading, setLoading] = useState(false);
    const [recordsPerUser, setRecordsPerUser] = useState<number | string>(100);
    const [timeZone, setTimeZone] = useState<string>(getUserTimeZone);
    const [startTime, setStartTime] = useState<string | null>(() =>
        defaultSearch.startTimestamp
            ? TimestampToDate(defaultSearch.startTimestamp).toISOString()
            : null
    );
    const [endTime, setEndTime] = useState<string | null>(() =>
        defaultSearch.endTimestamp
            ? TimestampToDate(defaultSearch.endTimestamp).toISOString()
            : null
    );

    useEffect(() => {
        if (opened) {
            setStartTime(
                defaultSearch.startTimestamp
                    ? TimestampToDate(defaultSearch.startTimestamp).toISOString()
                    : null
            );
            setEndTime(
                defaultSearch.endTimestamp
                    ? TimestampToDate(defaultSearch.endTimestamp).toISOString()
                    : null
            );
        }
    }, [opened, defaultSearch.startTimestamp, defaultSearch.endTimestamp]);

    const startTimePresets = useMemo(() => getStartTimePresets(), [opened]);
    const endTimePresets = useMemo(() => getEndTimePresets(), [opened]);

    const timeZoneOptions = useMemo(() => getTimeZoneOptions(timeZone), [timeZone]);

    // Basic columns (default all checked)
    const [includeTime, setIncludeTime] = useState(true);
    const [includeMediaUrls, setIncludeMediaUrls] = useState(true);

    // Advanced & metadata columns
    const [includeTags, setIncludeTags] = useState(true);
    const [includeFeedback, setIncludeFeedback] = useState(true);
    const [includeClientInfo, setIncludeClientInfo] = useState(false);
    const [includeTokenUsage, setIncludeTokenUsage] = useState(false);
    const [includeToolRecords, setIncludeToolRecords] = useState(false);

    const handleSelectAll = () => {
        setIncludeTime(true);
        setIncludeMediaUrls(true);
        setIncludeTags(true);
        setIncludeFeedback(true);
        setIncludeClientInfo(true);
        setIncludeTokenUsage(true);
        setIncludeToolRecords(true);
    };

    const handleResetDefaults = () => {
        setIncludeTime(true);
        setIncludeMediaUrls(true);
        setIncludeTags(true);
        setIncludeFeedback(true);
        setIncludeClientInfo(false);
        setIncludeTokenUsage(false);
        setIncludeToolRecords(false);
    };

    const handleExport = async () => {
        setLoading(true);
        try {
            const numRecords =
                typeof recordsPerUser === "number" && recordsPerUser > 0
                    ? recordsPerUser
                    : Number(recordsPerUser) || 100;

            const requestData: ChatBotHistoryExportRequest = {
                ...defaultSearch,
                timeZone,
                recordsPerUser: Math.max(1, Math.min(numRecords, 10000)),
                startTimestamp: startTime ? DateToTimestamp(startTime) : 0,
                endTimestamp: endTime ? DateToTimestamp(endTime) : 0,
                includeTime,
                includeMediaUrls,
                includeTags,
                includeFeedback,
                includeClientInfo,
                includeTokenUsage,
                includeToolRecords,
            };

            const response = await postApiChatBotHistoryExportCsv(requestData, {
                responseType: "blob",
            });

            const defaultUserName = (defaultSearch as any).userName;
            let fileName = defaultSearch.userId
                ? (defaultUserName ? `${defaultUserName}_${defaultSearch.userId}.csv` : `${defaultSearch.userId}.csv`)
                : `chat_history_export_${dayjs().format("YYYYMMDD_HHmmss")}.zip`;

            const disposition = response.headers?.["content-disposition"];
            if (disposition) {
                const match =
                    disposition.match(/filename\*?=(?:UTF-8'')?("?)(.+?)\1(?:;|$)/i) ||
                    disposition.match(/filename="?(.+?)"?($|;)/);
                if (match && match[2]) {
                    fileName = decodeURIComponent(match[2]);
                } else if (match && match[1]) {
                    fileName = decodeURIComponent(match[1]);
                }
            }

            BlobSaveToFile(response.data as unknown as Blob, fileName);
            notifications.show({
                title: "匯出成功",
                message: `已順利下載 ${fileName}`,
                color: "green",
            });
            onClose();
        } catch (error) {
            console.error("Export chat history failed", error);
            notifications.show({
                title: "匯出失敗",
                message: "產生聊天紀錄檔案時發生錯誤，請稍後再試",
                color: "red",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title={title} size="lg">
            <Stack gap="md">
                <NumberInput
                    label="每位使用者最多匯出筆數"
                    description="從最新紀錄往前計算（預設 100 筆，上限 10,000 筆）"
                    value={recordsPerUser}
                    onChange={setRecordsPerUser}
                    min={1}
                    max={10000}
                    step={50}
                />

                <Select
                    label="匯出時區 (Timezone)"
                    description="CSV 內「時間」欄位將依照此時區換算格式化"
                    searchable
                    clearable={false}
                    limit={50}
                    data={timeZoneOptions}
                    value={timeZone}
                    onChange={(val) => val && setTimeZone(val)}
                />

                <Group grow>
                    <DateTimePicker
                        label="開始時間（可選）"
                        placeholder="請選擇起始時間"
                        valueFormat="YYYY/MM/DD HH:mm"
                        value={startTime}
                        onChange={setStartTime}
                        clearable
                        presets={startTimePresets}
                    />
                    <DateTimePicker
                        label="結束時間（可選）"
                        placeholder="請選擇結束時間"
                        valueFormat="YYYY/MM/DD HH:mm"
                        value={endTime}
                        onChange={setEndTime}
                        clearable
                        presets={endTimePresets}
                    />
                </Group>

                <Group justify="space-between" align="center">
                    <Text fw={500} size="sm">
                        自訂匯出欄位
                    </Text>
                    <Group gap="xs">
                        <Button size="compact-xs" variant="subtle" onClick={handleSelectAll}>
                            全選
                        </Button>
                        <Button size="compact-xs" variant="subtle" color="gray" onClick={handleResetDefaults}>
                            重設預設值
                        </Button>
                    </Group>
                </Group>

                <Paper withBorder p="sm" radius="sm">
                    <Stack gap="xs">
                        <Text size="xs" fw={600} c="dimmed">
                            基本對話欄位
                        </Text>
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                            <Checkbox
                                label="時間 (Time)"
                                checked={includeTime}
                                onChange={(e) => setIncludeTime(e.currentTarget.checked)}
                            />
                            <Checkbox
                                label="媒體連結 (MediaUrls)"
                                checked={includeMediaUrls}
                                onChange={(e) => setIncludeMediaUrls(e.currentTarget.checked)}
                            />
                        </SimpleGrid>

                        <Divider my={4} />

                        <Text size="xs" fw={600} c="dimmed">
                            進階與診斷欄位
                        </Text>
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                            <Checkbox
                                label="標籤 (Tags)"
                                checked={includeTags}
                                onChange={(e) => setIncludeTags(e.currentTarget.checked)}
                            />
                            <Checkbox
                                label="滿意度評價與評論 (Feedback)"
                                checked={includeFeedback}
                                onChange={(e) => setIncludeFeedback(e.currentTarget.checked)}
                            />
                            <Checkbox
                                label="客戶端連線資訊 (Client IP / User-Agent)"
                                checked={includeClientInfo}
                                onChange={(e) => setIncludeClientInfo(e.currentTarget.checked)}
                            />
                            <Checkbox
                                label="Token 消耗統計 (Token Usage)"
                                checked={includeTokenUsage}
                                onChange={(e) => setIncludeTokenUsage(e.currentTarget.checked)}
                            />
                            <Checkbox
                                label="工具調用明細 (Tool Records)"
                                checked={includeToolRecords}
                                onChange={(e) => setIncludeToolRecords(e.currentTarget.checked)}
                            />
                        </SimpleGrid>
                    </Stack>
                </Paper>

                <Text size="xs" c="dimmed">
                    {defaultSearch.userId
                        ? "單人匯出將下載 UTF-8 (含 BOM) CSV 檔案，可直接使用 Microsoft Excel 或各類分析工具開啟。"
                        : "搜尋匯出將為每位使用者獨立生成一個 CSV 檔案並打包為 ZIP 壓縮檔，避免不同使用者紀錄混雜。"}
                </Text>

                <Group justify="flex-end" mt="xs">
                    <Button variant="default" onClick={onClose} disabled={loading}>
                        取消
                    </Button>
                    <Button
                        leftSection={<IconFileExport size={16} />}
                        onClick={handleExport}
                        loading={loading}
                    >
                        開始匯出
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
